import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { MagnifyingGlass, DownloadSimple, ArrowRight, User, Buildings, Clock, Tag as TagIcon } from "@phosphor-icons/react";

const STAGE_TONES = {
  new_lead: "border-slate-300 bg-slate-50",
  qualification: "border-sky-300 bg-sky-50",
  discovery_scheduled: "border-indigo-300 bg-indigo-50",
  discovery_completed: "border-violet-300 bg-violet-50",
  proposal_sent: "border-purple-300 bg-purple-50",
  waiting_payment: "border-amber-300 bg-amber-50",
  payment_confirmed: "border-emerald-300 bg-emerald-50",
  workspace_activated: "border-teal-300 bg-teal-50",
  customer_success: "border-green-300 bg-green-50",
  closed: "border-zinc-300 bg-zinc-50",
  lost: "border-rose-300 bg-rose-50",
};

const PRIORITY_DOT = {
  low: "bg-slate-400",
  medium: "bg-sky-500",
  high: "bg-amber-500",
  urgent: "bg-rose-500",
};

export default function CRMPipeline() {
  const [leads, setLeads] = useState([]);
  const [config, setConfig] = useState(null);
  const [q, setQ] = useState("");
  const [priority, setPriority] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [ldRes, cfgRes] = await Promise.all([
        api.get(`/crm/leads?limit=500${q ? `&q=${encodeURIComponent(q)}` : ""}${priority ? `&priority=${priority}` : ""}`),
        config ? Promise.resolve({ data: config }) : api.get("/crm/config"),
      ]);
      setLeads(ldRes.data || []);
      if (!config) setConfig(cfgRes.data);
    } catch (err) {
      toast.error(extractApiError(err, "Could not load CRM pipeline"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, priority]);

  const byStage = useMemo(() => {
    const map = {};
    (config?.stages || []).forEach((s) => (map[s.key] = []));
    leads.forEach((l) => {
      const key = l.status in map ? l.status : "new_lead";
      map[key].push(l);
    });
    return map;
  }, [leads, config]);

  const total = leads.length;

  const downloadCsv = async () => {
    try {
      const resp = await api.get("/crm/leads/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "orixa-leads.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10" data-testid="crm-pipeline-page">
      <PageHeader
        eyebrow="CRM"
        title="Sales pipeline"
        subtitle={`${total} lead${total === 1 ? "" : "s"} across ${config?.stages?.length || 0} stages`}
        action={
          <button
            onClick={downloadCsv}
            className="inline-flex items-center gap-2 border border-border bg-card rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-secondary transition-colors"
            data-testid="crm-export-csv"
          >
            <DownloadSimple size={14} weight="bold" /> Export CSV
          </button>
        }
      />

      {/* Filters */}
      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, company…"
            className="w-full bg-card border border-border rounded-lg pl-10 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
            data-testid="crm-search"
          />
        </div>
        <select
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          className="bg-card border border-border rounded-lg px-3 py-2.5 text-sm"
          data-testid="crm-priority-filter"
        >
          <option value="">All priorities</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {/* Board */}
      <div className="mt-8 overflow-x-auto pb-8 -mx-4 px-4 sm:mx-0 sm:px-0">
        <div className="flex gap-4 min-w-max">
          {(config?.stages || []).map((s) => {
            const items = byStage[s.key] || [];
            return (
              <div key={s.key} className="w-72 flex-shrink-0" data-testid={`crm-col-${s.key}`}>
                <div className={`px-4 py-3 rounded-t-xl border ${STAGE_TONES[s.key] || "bg-secondary border-border"} border-b-0`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold">{s.label}</div>
                    <div className="text-xs font-mono bg-white/70 px-2 py-0.5 rounded-full">{items.length}</div>
                  </div>
                </div>
                <div className="border border-t-0 border-border bg-card rounded-b-xl p-2 space-y-2 min-h-[200px]">
                  {loading && <div className="text-xs text-muted-foreground text-center py-8">Loading…</div>}
                  {!loading && items.length === 0 && <div className="text-xs text-muted-foreground text-center py-8">Empty</div>}
                  {items.map((l) => <LeadCard key={l.id} l={l} />)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function LeadCard({ l }) {
  const priorityDot = PRIORITY_DOT[l.priority] || PRIORITY_DOT.medium;
  return (
    <Link
      to={`/crm/leads/${l.id}`}
      className="block bg-white border border-border rounded-lg p-3 hover:border-primary/40 hover:card-elev transition-all group"
      data-testid={`crm-lead-card-${l.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">{l.full_name || "—"}</div>
          <div className="text-xs text-muted-foreground truncate mt-0.5 inline-flex items-center gap-1">
            <Buildings size={11} /> {l.company_name || "—"}
          </div>
        </div>
        <span className={`h-2 w-2 rounded-full mt-1.5 flex-shrink-0 ${priorityDot}`} title={`Priority: ${l.priority}`} />
      </div>
      {l.country && (
        <div className="mt-2 text-[11px] text-muted-foreground inline-flex items-center gap-1">
          <TagIcon size={10} /> {l.country} · {l.industry || "—"}
        </div>
      )}
      <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
        <span className="inline-flex items-center gap-1"><Clock size={10} /> {new Date(l.created_at).toLocaleDateString()}</span>
        {l.assigned_to && <span className="inline-flex items-center gap-1"><User size={10} /> {l.assigned_to.split("@")[0]}</span>}
        <ArrowRight size={11} className="group-hover:translate-x-0.5 transition-transform text-primary" />
      </div>
    </Link>
  );
}
